'use client'

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Link } from 'react-router-dom'

interface Props {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo)
        // Could send to Sentry here
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            return (
                <div className="!flex !min-h-screen !items-center !justify-center !bg-white !px-4 !py-20">
                    <Card className="!max-w-md !w-full !p-8 !text-center">
                        <h1 className="!text-2xl font-heading font-semibold !text-slate-900 !mb-4">
                            Something went wrong
                        </h1>
                        <p className="!text-slate-600 !mb-6">
                            {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
                        </p>
                        <div className="!flex !gap-3 !justify-center">
                            <Button
                                onClick={() => {
                                    this.setState({ hasError: false, error: null })
                                    if (typeof window !== 'undefined') {
                                        window.location.reload()
                                    }
                                }}
                                variant="default"
                            >
                                Reload page
                            </Button>
                            <Button asChild variant="outline">
                                <Link to="/">Go home</Link>
                            </Button>
                        </div>
                    </Card>
                </div>
            )
        }

        return this.props.children
    }
}
